// Unified Numerology Module
// Single source of truth for life path calculations and compatibility

// Master Numbers (proper ones only)
export const MASTER_NUMBERS = [11, 22, 28, 33];

// Reduce a number to a single digit OR a master number (whichever comes first).
function reduceWithMaster(num) {
  while (num > 9 && !MASTER_NUMBERS.includes(num)) {
    num = num.toString().split('').reduce((s, d) => s + parseInt(d, 10), 0);
  }
  return num;
}

function reduceFully(num) {
  while (num > 9) {
    num = num.toString().split('').reduce((s, d) => s + parseInt(d, 10), 0);
  }
  return num;
}

export function formatLifePathValue(value) {
  return value === 28 ? '(28)1' : `${value}`;
}

export function getLifePathDisplayValue(lifePathDataOrResult, fallbackValue) {
  const result = lifePathDataOrResult?.primary || lifePathDataOrResult;
  const value = fallbackValue ?? result?.value;

  if (result?.displayValue) {
    return result.displayValue;
  }

  return value == null ? '' : formatLifePathValue(value);
}

export function shouldShowAlternateLifePath(lifePathData) {
  if (!lifePathData?.primary || !lifePathData?.alternate) {
    return false;
  }

  return getLifePathDisplayValue(lifePathData.primary) !== getLifePathDisplayValue(lifePathData.alternate);
}

function formatReduction(parts, total, value) {
  const expression = parts.join('+');
  return total !== value
    ? `${expression}=${total}/${formatLifePathValue(value)}`
    : `${expression}=${formatLifePathValue(value)}`;
}

function getNonZeroDigits(num) {
  return `${num}`
    .split('')
    .map(d => parseInt(d, 10))
    .filter(d => d !== 0);
}

function buildCalculationResult(parts, method, preservesMasters = true) {
  const total = parts.reduce((sum, part) => sum + part, 0);
  const value = preservesMasters ? reduceWithMaster(total) : reduceFully(total);
  const isMaster = MASTER_NUMBERS.includes(value);
  const isPure = isMaster && value === total;
  const displayValue = total !== value
    ? `${total}/${formatLifePathValue(value)}`
    : formatLifePathValue(value);

  return {
    value,
    isPure,
    reduction: formatReduction(parts, total, value),
    method,
    total,
    parts,
    displayValue
  };
}

// Back-compat alias used by getNameNumber below.
const reduceToSingleDigit = reduceWithMaster;

// Life Path Number Meanings
export const LIFE_PATH_MEANINGS = {
  1: 'The Leader - Independent, pioneering, ambitious, and innovative. Natural born leaders who create their own path.',
  2: 'The Cooperator - Diplomatic, sensitive, peaceful, and supportive. Masters of collaboration and harmony.',
  3: 'The Creative Communicator - Artistic, expressive, optimistic, and social. Brings joy and creativity to the world.',
  4: 'The Builder - Practical, organized, hardworking, and reliable. Creates solid foundations for lasting success.',
  5: 'The Freedom Seeker - Adventurous, curious, versatile, and dynamic. Thrives on change and new experiences.',
  6: 'The Nurturer - Caring, responsible, protective, and service-oriented. Natural healers and family-focused.',
  7: 'The Seeker - Analytical, spiritual, introspective, and wise. Searches for deeper truths and meaning.',
  8: 'The Achiever - Ambitious, authoritative, business-minded, and success-oriented. Masters material world.',
  9: 'The Universal Humanitarian - Adaptable and versatile, containing the essence of all numbers 1-8. Compassionate, generous, idealistic, and globally conscious. Naturally understands all types of people and serves the greater good.',
  11: 'The Intuitive - Highly sensitive, inspirational, visionary, and spiritually aware. Master number with special gifts.',
  22: 'The Master Builder - Practical visionary who can turn dreams into reality on a grand scale. Rare master number.',
  28: 'The Master Leader - Combines the leadership qualities of 1 with the spiritual depth of master numbers. Natural born leaders with heightened intuition and the ability to inspire others on a grand scale.',
  33: 'The Master Teacher - Combines wisdom of 11 and building power of 22. Ultimate healer and spiritual guide.'
};

// Compatibility matrix (standardized scoring buckets: 95/85/50/25/0)
export const COMPATIBILITY_MATRIX = {
  1: { compatible: [3, 5], neutral: [2, 6, 7, 11, 33], challenging: [4, 8, 22], enemy: [9] },
  2: { compatible: [4, 8], neutral: [1, 3, 6], challenging: [5, 7, 9, 22], enemy: [11, 33] },
  3: { compatible: [1, 5, 6, 9, 33], neutral: [2, 7], challenging: [4, 8, 11, 22], enemy: [] },
  4: { compatible: [2, 6, 8, 22], neutral: [1, 7, 11, 33], challenging: [3, 5, 9], enemy: [] },
  5: { compatible: [1, 3, 7, 9], neutral: [6, 33], challenging: [2, 4, 8, 11, 22], enemy: [] },
  6: { compatible: [3, 4, 9, 11, 22, 33], neutral: [1, 2, 5], challenging: [7, 8], enemy: [] },
  7: { compatible: [5, 7], neutral: [1, 4, 9, 22], challenging: [2, 3, 6, 8, 11, 33], enemy: [] },
  8: { compatible: [2, 4, 6, 22], neutral: [9, 11, 33], challenging: [1, 3, 5, 7], enemy: [] },
  9: { compatible: [3, 5, 6, 11, 33], neutral: [7, 8, 22], challenging: [2, 4], enemy: [1] },
  11: { compatible: [4, 6, 8, 9], neutral: [1, 7], challenging: [3, 5, 22], enemy: [2, 33] },
  22: { compatible: [4, 6, 8], neutral: [7, 9], challenging: [1, 2, 3, 5, 11], enemy: [] },
  28: { compatible: [3, 5, 7, 9], neutral: [2, 6, 11, 33], challenging: [4, 8, 22], enemy: [] },
  33: { compatible: [3, 6, 9], neutral: [1, 4, 5, 7, 8], challenging: [], enemy: [2, 11] }
};

function parseDateParts(dateString) {
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    throw new Error('Invalid date format. Expected YYYY-MM-DD');
  }
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    throw new Error('Invalid date components');
  }
  return { year, month, day };
}

// Component-wise method (alternate): reduce month, day, and year each to a
// single digit or master, then sum and reduce while preserving masters.
export function getLifePathComponentWise(dateString) {
  const { year, month, day } = parseDateParts(dateString);

  const monthReduced = reduceWithMaster(month);
  const dayReduced = reduceWithMaster(day);
  const yearReduced = reduceWithMaster(year);

  const sum = monthReduced + dayReduced + yearReduced;
  const value = reduceWithMaster(sum);

  const isMaster = MASTER_NUMBERS.includes(value);
  const isPure = isMaster && value === sum;
  const parts = [monthReduced, dayReduced, yearReduced];

  const reduction = formatReduction(parts, sum, value);

  return {
    value,
    isPure,
    reduction,
    method: 'component-wise',
    total: sum,
    parts,
    displayValue: formatLifePathValue(value)
  };
}

// All-digits method (primary/pure): sum every digit of MM/DD/YYYY, then reduce.
export function getLifePathAllDigits(dateString) {
  const { year, month, day } = parseDateParts(dateString);

  const digits = [
    ...getNonZeroDigits(month),
    ...getNonZeroDigits(day),
    ...getNonZeroDigits(year)
  ];

  return buildCalculationResult(digits, 'all-digits');
}

export function getBirthdayCombinationBreakdown(dateString) {
  const { year, month, day } = parseDateParts(dateString);
  const components = {
    month: {
      digits: getNonZeroDigits(month),
      reduced: reduceFully(month)
    },
    day: {
      digits: getNonZeroDigits(day),
      reduced: reduceFully(day)
    },
    year: {
      digits: getNonZeroDigits(year),
      reduced: reduceFully(year)
    }
  };
  const combinations = [
    { key: 'month-year', label: 'Month + Year', components: ['month', 'year'] },
    { key: 'month-day', label: 'Month + Day', components: ['month', 'day'] },
    { key: 'year-day', label: 'Year + Day', components: ['day', 'year'] }
  ];

  return combinations.map(combination => {
    const classicalParts = combination.components.flatMap(component => components[component].digits);
    const reducedParts = combination.components.map(component => components[component].reduced);

    return {
      key: combination.key,
      label: combination.label,
      classical: buildCalculationResult(classicalParts, 'classical'),
      reduced: buildCalculationResult(reducedParts, 'reduced', false)
    };
  });
}

export function getBirthdayCompositeCalculation(dateString) {
  const { year, month, day } = parseDateParts(dateString);
  const monthDay = buildCalculationResult([month, day], 'month-day-calendar');
  const dayYear = buildCalculationResult([day, ...getNonZeroDigits(year)], 'day-year-classical');
  const total = buildCalculationResult([monthDay.value, dayYear.value], 'birthday-composite');

  return {
    label: 'Birthday Composite',
    monthDay,
    dayYear,
    total
  };
}

// Primary calculator. Returns the all-digits result at the top level
// (consumers read `.value`, `.isPure`, `.reduction`) and exposes both methods
// under `.primary` and `.alternate` so the UI can display the disagreement.
export function getLifePathNumber(dateString) {
  const primary = getLifePathAllDigits(dateString);
  const alternate = getLifePathComponentWise(dateString);
  return {
    value: primary.value,
    isPure: primary.isPure,
    reduction: primary.reduction,
    displayValue: primary.displayValue,
    primary,
    alternate,
    combinations: getBirthdayCombinationBreakdown(dateString),
    birthdayComposite: getBirthdayCompositeCalculation(dateString)
  };
}

export function getNameNumber(name) {
  const values = {
    'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8, 'I': 9,
    'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'O': 6, 'P': 7, 'Q': 8, 'R': 9,
    'S': 1, 'T': 2, 'U': 3, 'V': 4, 'W': 5, 'X': 6, 'Y': 7, 'Z': 8
  };

  let sum = 0;
  for (let char of name.toUpperCase()) {
    if (values[char]) {
      sum += values[char];
    }
  }

  return reduceToSingleDigit(sum);
}

// Compatibility scoring with standardized buckets: 95/85/50/25/0
export function calculateLifePathCompatibility(lifePath1, lifePath2) {
  if (!lifePath1 || !lifePath2) return { score: 50, level: 'neutral', description: 'Invalid life paths provided' };

  // Same life path - highest compatibility
  if (lifePath1 === lifePath2) {
    return {
      score: 95,
      level: 'same',
      description: `Perfect understanding and shared life purpose with another Life Path ${lifePath1}.`
    };
  }

  const compatibility = COMPATIBILITY_MATRIX[lifePath1];
  if (!compatibility) {
    return { score: 50, level: 'neutral', description: 'Compatibility data not available for this life path' };
  }

  // Check enemy relationships
  if (compatibility.enemy && compatibility.enemy.includes(lifePath2)) {
    return {
      score: 0,
      level: 'enemy',
      description: `Life Path ${lifePath1} and ${lifePath2} represent opposite life purposes with fundamental conflicts.`
    };
  }

  // Check compatible relationships
  if (compatibility.compatible.includes(lifePath2)) {
    return {
      score: 85,
      level: 'compatible',
      description: `Life Path ${lifePath1} and ${lifePath2} create natural understanding and mutual support.`
    };
  }

  // Check neutral relationships
  if (compatibility.neutral.includes(lifePath2)) {
    return {
      score: 50,
      level: 'neutral',
      description: `Life Path ${lifePath1} and ${lifePath2} have moderate potential with understanding and effort.`
    };
  }

  // Default to challenging
  return {
    score: 25,
    level: 'challenging',
    description: `Life Path ${lifePath1} and ${lifePath2} require patience and conscious effort to harmonize.`
  };
}

// Rich compatibility descriptions (from legacy model)
export function getDetailedCompatibilityDescription(lifePath1, lifePath2, name1, name2) {
  const compat = calculateLifePathCompatibility(lifePath1, lifePath2);
  let description = `Life Path ${lifePath1} & Life Path ${lifePath2}\n\n`;

  // Add life path meanings
  description += `Life Path ${lifePath1}: ${LIFE_PATH_MEANINGS[lifePath1]}\n`;
  description += `Life Path ${lifePath2}: ${LIFE_PATH_MEANINGS[lifePath2]}\n\n`;

  // Add specific compatibility descriptions
  if (compat.level === 'same') {
    description += "🟢 SAME LIFE PATH: Perfect understanding and natural harmony.";
  } else if (compat.level === 'compatible') {
    description += "🟢 HIGHLY COMPATIBLE LIFE PATHS: ";

    // Specific combinations with rich descriptions
    if ((lifePath1 === 1 && lifePath2 === 5) || (lifePath1 === 5 && lifePath2 === 1)) {
      description += "1 and 5 create an exciting partnership of leadership and adventure. 1 provides direction while 5 brings variety and excitement. Together you can achieve great things while keeping life interesting.";
    } else if ((lifePath1 === 2 && lifePath2 === 8) || (lifePath1 === 8 && lifePath2 === 2)) {
      description += "2 and 8 form a powerhouse combination. 2's diplomacy and cooperation perfectly complement 8's business acumen and drive. This partnership can achieve both financial success and emotional fulfillment.";
    } else if ((lifePath1 === 3 && lifePath2 === 6) || (lifePath1 === 6 && lifePath2 === 3)) {
      description += "3 and 6 create a nurturing, creative bond. 3's artistic expression is supported by 6's caring nature, while 6 finds joy in 3's optimism and creativity. A harmonious, family-oriented partnership.";
    } else if ((lifePath1 === 4 && lifePath2 === 8) || (lifePath1 === 8 && lifePath2 === 4)) {
      description += "4 and 8 build empires together. Both value hard work, structure, and material success. 4 provides the methodical planning while 8 provides the ambitious vision. A practical, goal-oriented partnership.";
    } else {
      description += "Your life paths naturally complement each other, creating understanding, mutual support, and shared growth opportunities.";
    }
  } else if (compat.level === 'neutral') {
    description += "🟠 NEUTRAL LIFE PATH COMPATIBILITY: ";
    description += "Your life paths can work well together with conscious effort and mutual understanding. You may have different approaches but can learn valuable lessons from each other.";
  } else if (compat.level === 'enemy') {
    description += "🔴 ENEMY LIFE PATHS: ";
    description += "Your life paths represent opposite approaches to life with fundamental differences in values and priorities.";
  } else { // challenging
    description += "🟡 CHALLENGING LIFE PATH COMBINATION: ";

    if ((lifePath1 === 1 && lifePath2 === 8) || (lifePath1 === 8 && lifePath2 === 1)) {
      description += "Both 1 and 8 are natural leaders, which can create power struggles. Success comes when you learn to take turns leading and support each other's ambitions rather than competing.";
    } else if ((lifePath1 === 7 && lifePath2 === 3) || (lifePath1 === 3 && lifePath2 === 7)) {
      description += "7's need for solitude can clash with 3's social nature. 7 may find 3 too superficial, while 3 may find 7 too serious. Balance comes through respecting each other's different needs.";
    } else {
      description += "Your life paths represent different approaches to life that require patience, understanding, and conscious effort to harmonize successfully.";
    }
  }

  description += "\n\n";

  // Master number insights
  const hasMaster1 = MASTER_NUMBERS.includes(lifePath1);
  const hasMaster2 = MASTER_NUMBERS.includes(lifePath2);

  if (hasMaster1 || hasMaster2) {
    description += "🌟 MASTER NUMBER PRESENCE: ";
    if (hasMaster1 && hasMaster2) {
      description += "Both partners carry master number energy, indicating old souls with special gifts and heightened responsibilities.";
    } else {
      if (lifePath1 === 11 || lifePath2 === 11) {
        description += "The presence of Life Path 11 brings heightened intuition and spiritual awareness to this relationship. ";
      }
      if (lifePath1 === 22 || lifePath2 === 22) {
        description += "Life Path 22 adds the power to manifest dreams into reality on a grand scale. ";
      }
      if (lifePath1 === 33 || lifePath2 === 33) {
        description += "Life Path 33 brings the energy of the master teacher and healer. ";
      }
      description += "Master numbers indicate old souls with special gifts and heightened responsibilities.";
    }
    description += "\n\n";
  }

  // Relationship guidance based on combined energy
  description += "💡 RELATIONSHIP GUIDANCE: ";

  const sumPath = lifePath1 + lifePath2;
  if (sumPath <= 10) {
    description += "Your combined energy focuses on building strong foundations and taking practical steps toward shared goals.";
  } else if (sumPath <= 16) {
    description += "Together you create a balanced dynamic of personal growth and mutual support.";
  } else {
    description += "Your partnership has high energy and potential for significant impact on others and the world.";
  }

  return {
    score: compat.score,
    level: compat.level,
    description
  };
}

// Generate matches for a given life path (used in discovery/matching)
export function generateNumerologyMatches(userLifePath) {
  const matches = [];
  const allLifePaths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22, 28, 33];

  for (let lifePath of allLifePaths) {
    const compat = calculateLifePathCompatibility(userLifePath, lifePath);
    let description = `Life Path ${lifePath}: ${LIFE_PATH_MEANINGS[lifePath]}\n\n`;

    if (compat.level === 'same') {
      description += `🟢 SAME LIFE PATH: Perfect understanding and shared life purpose with another Life Path ${lifePath}.`;
    } else if (compat.level === 'enemy') {
      description += `🔴 ENEMY NUMBERS: Life Path ${userLifePath} and ${lifePath} represent opposite life purposes with fundamental conflicts.`;
    } else if (compat.level === 'compatible') {
      description += `🟢 HIGHLY COMPATIBLE: Life Path ${userLifePath} and ${lifePath} create natural understanding and mutual support.`;
    } else if (compat.level === 'neutral') {
      description += `🟠 MODERATE: Life Path ${userLifePath} and ${lifePath} can work well together with understanding and effort.`;
    } else {
      description += `🟡 CHALLENGING: Life Path ${userLifePath} and ${lifePath} require patience and conscious effort to harmonize.`;
    }

    matches.push({
      lifePath: lifePath,
      score: compat.score,
      level: compat.level,
      description: description
    });
  }

  return matches.sort((a, b) => b.score - a.score);
}

// Default export for backward compatibility
export default {
  MASTER_NUMBERS,
  LIFE_PATH_MEANINGS,
  COMPATIBILITY_MATRIX,
  formatLifePathValue,
  getLifePathDisplayValue,
  shouldShowAlternateLifePath,
  getBirthdayCombinationBreakdown,
  getBirthdayCompositeCalculation,
  getLifePathNumber,
  getLifePathComponentWise,
  getLifePathAllDigits,
  getNameNumber,
  calculateLifePathCompatibility,
  getDetailedCompatibilityDescription,
  generateNumerologyMatches
};
